import React, { useMemo, useState } from 'react';
import { PoundSterling, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

/**
 * Prompts the agent for the amount of a partial payment against a policy.
 * - Shows Total (policy.amount), Already Paid (sum of prior partials), Remaining
 * - The amount cannot exceed the current outstanding balance
 * - Auto-flags 'this pays the balance in full' when the entered amount matches
 * - onConfirm({ amount, note }) is invoked; parent decides how to record it
 */
const PartialPaymentDialog = ({ open, policy, onClose, onConfirm }) => {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');

  const summary = useMemo(() => {
    const total = Number(policy?.amount || 0);
    const prev = Array.isArray(policy?.partialPayments) ? policy.partialPayments : [];
    const paid = prev.reduce((s, pp) => s + Number(pp.amount || 0), 0);
    const outstanding = Math.max(0, +(total - paid).toFixed(2));
    return { total, paid, outstanding, prev };
  }, [policy]);

  if (!open || !policy) return null;

  const submit = (e) => {
    e.preventDefault();
    const n = parseFloat(amount);
    if (isNaN(n) || n <= 0) {
      setErr('Please enter the amount the customer has paid.');
      return;
    }
    if (n > summary.outstanding + 0.005) {
      setErr(`This exceeds the outstanding balance of £${summary.outstanding.toFixed(2)}.`);
      return;
    }
    onConfirm({ amount: Number(n.toFixed(2)), note: note.trim() });
  };

  const remainingAfter = Math.max(0, +(summary.outstanding - (parseFloat(amount) || 0)).toFixed(2));
  const willBeFullyPaid = parseFloat(amount) && remainingAfter <= 0.005;

  return (
    <div
      data-testid="partial-payment-dialog"
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        noValidate
        className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl border border-[#1F0068]/10"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 text-[#FF5C39]">
              <PoundSterling className="w-5 h-5" />
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold">Record partial payment</div>
            </div>
            <h2 className="text-xl font-bold text-[#1F0068] mt-1">Plan {policy.planNumber}</h2>
            <p className="text-xs text-[#1F0068]/70 mt-1">How much has {policy.firstName} paid this time?</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-[#1F0068]/60 hover:text-[#1F0068]"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4 text-[11px]">
          <div className="bg-[#FFF6EB] rounded-xl p-3">
            <div className="text-[9px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Total</div>
            <div data-testid="partial-summary-total" className="text-base font-extrabold text-[#1F0068]">£ {summary.total.toFixed(2)}</div>
          </div>
          <div className="bg-[#E8DDFF] rounded-xl p-3">
            <div className="text-[9px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Paid so far</div>
            <div data-testid="partial-summary-paid" className="text-base font-extrabold text-[#1F0068]">£ {summary.paid.toFixed(2)}</div>
          </div>
          <div className="bg-[#FFEDD6] rounded-xl p-3">
            <div className="text-[9px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Outstanding</div>
            <div data-testid="partial-summary-outstanding" className="text-base font-extrabold text-[#FF5C39]">£ {summary.outstanding.toFixed(2)}</div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-[#1F0068] font-semibold">How much has the customer paid?</Label>
            <Input
              data-testid="partial-payment-amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => { setAmount(e.target.value); setErr(''); }}
              placeholder={`e.g. ${(summary.outstanding * 0.5 || 60).toFixed(2)}`}
              className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10"
              autoFocus
            />
            {parseFloat(amount) > 0 && (
              <p className="text-[10px] text-[#1F0068]/70 mt-1">
                Remaining balance after this payment: <b className={willBeFullyPaid ? 'text-[#00b67a]' : 'text-[#FF5C39]'}>£{remainingAfter.toFixed(2)}</b>
                {willBeFullyPaid && <> — status will change to <b>Paid</b> and a receipt email will be sent.</>}
              </p>
            )}
          </div>
          <div>
            <Label className="text-[#1F0068] font-semibold">Reference / note (optional)</Label>
            <Input
              data-testid="partial-payment-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Bank transfer reference, cheque number, etc."
              className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10"
            />
          </div>
          {err && <div data-testid="partial-payment-error" className="text-xs text-[#FF5C39] font-semibold">{err}</div>}
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button
            data-testid="partial-payment-confirm"
            type="submit"
            className="rounded-full bg-[#FF5C39] hover:bg-[#e0492b] text-white gap-1.5"
          >
            <PoundSterling className="w-4 h-4" /> Record payment
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PartialPaymentDialog;
